import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-apollo';

import { SkeletonLoader, StaticContext } from '@sm/webassets';
import { defineMessages, T } from '@sm/intl';
import { Typography } from '@sm/wds-react';
import { marketingEmailPreferencesQuery } from '../EmailPreferences.graphql';
import EmailPreferencesList from '../EmailPreferencesList';

const COPY = defineMessages({
  EMAIL_PREFERENCES_HEADER: {
    id: 'User.EmailPreferencesQuery.Header',
    defaultMessage: 'Communication Preferences',
    description: '[Type: Header][Vis.: high] - Marketing email preferences header',
  },
  EMAIL_PREFERENCES_DESCRIPTION: {
    id: 'User.EmailPreferencesQuery.Description',
    defaultMessage:
      "Select the types of messages you’d like to receive. As outlined in our <a  target='_blank' href='/mp/legal/privacy-policy/?ut_source=email_unsubscribe'>Privacy Policy</a>, we don’t sell or rent email addresses.",
    description: '[Type: Header][Vis.: high] - Marketing email preferences description',
  },
  EMAIL_PREFERENCES_ERROR_HEADER: {
    id: 'User.EmailPreferencesQuery.ErrorHeader',
    defaultMessage: 'Something went wrong',
    description: '[Type: Header][Vis.: med] - Marketing email preferences error header',
  },
  EMAIL_PREFERENCES_NO_EMAIL_ERROR: {
    id: 'User.EmailPreferencesQuery.NoEmailError',
    defaultMessage:
      "<a href='/user/sign-in/?ep=%2Fuser%2Femail-preferences%2F'>Sign in</a> and go to My Account to unsubscribe. If you don't have an account, " +
      "<a href='https://help.surveymonkey.com/contact?ut_source=email_unsubscribe'>contact us</a> for help unsubscribing.",
    description: '[Type: Paragraph][Vis.: med] - Marketing email preferences error',
  },
  EMAIL_PREFERENCES_GENERAL_ERROR: {
    id: 'User.EmailPreferencesQuery.GeneralError',
    defaultMessage: 'Please try again',
    description: '[Type: Paragraph][Vis.: med] - Marketing email preferences error',
  },
});

const ErrorMessage = ({ children }) => (
  <div>
    <Typography variant="pageTitle" className="wds-m-b-4">
      <T desc={COPY.EMAIL_PREFERENCES_ERROR_HEADER} />
    </Typography>
    <Typography>{children}</Typography>
  </div>
);

ErrorMessage.propTypes = {
  children: PropTypes.node.isRequired,
};

const Loading = () => (
  <SkeletonLoader data-testid="EmailPreferencesQuery__SkeletonLoader" height={740} width={660}>
    <rect x="0" y="0" rx="0" ry="0" width="660" height="40" />
    <rect x="0" y="54" rx="0" ry="0" width="660" height="12" />
    <rect x="0" y="80" rx="0" ry="0" width="400" height="12" />
    <rect x="0" y="140" rx="0" ry="0" width="660" height="600" />
  </SkeletonLoader>
);

const EmailPreferencesQuery = () => {
  const { pageOptions } = useContext(StaticContext);
  const { encryptedEmail } = pageOptions;

  const { loading, error, data } = useQuery(marketingEmailPreferencesQuery, {
    variables: { encryptedEmail },
  });

  if (!encryptedEmail) {
    return (
      <ErrorMessage>
        <T desc={COPY.EMAIL_PREFERENCES_NO_EMAIL_ERROR} html />
      </ErrorMessage>
    );
  }

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <ErrorMessage>
        <T desc={COPY.EMAIL_PREFERENCES_GENERAL_ERROR} />
      </ErrorMessage>
    );
  }

  return (
    <div>
      <Typography variant="sectionTitle">
        <T desc={COPY.EMAIL_PREFERENCES_HEADER} />
      </Typography>
      <Typography className="wds-m-t-4 wds-m-b-6">
        <T desc={COPY.EMAIL_PREFERENCES_DESCRIPTION} html />
      </Typography>
      <EmailPreferencesList emailPreferences={data.marketingEmailPreferences} {...pageOptions} />
    </div>
  );
};

export default EmailPreferencesQuery;
